import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { useSelector } from "react-redux";
import BroadcastContainer from "modules/broadcast/BroadcastContainer";
import {
  selectBroadcastTransitionInProgress,
  selectIsInBroadcast,
} from "modules/broadcast/redux/selectors";
import ConversationContainer from "modules/conversation/components/Container/Container";
import { useI18n } from "i18n";
import { useCallback, useEffect } from "react";
import { useAppDispatch } from "store/hooks";
import useBroadcastWhiteboardActions from "modules/broadcastWhiteboard/hooks/useBroadcastWhiteboardActions";
import useRoomWhiteboardActions from "modules/roomWhiteboard/hooks/useRoomWhiteboardActions";
import { Actions, trackActionEnd } from "modules/monitoring";
import { selectCurrentTheaterId } from "modules/theater/selectors";
import { selectCurrentRoomId } from "store/rooms";
import { usePrevious } from "helpers/reactHooksUtils";
import { hideCaptions, TranscriptionSubscriber } from "modules/transcription";
import { hideLiveCaptions } from "modules/transcription/redux";
import { LiveStreamSubscriber } from "modules/liveStream/components";
import { selectIsEventManager } from "modules/event/selectors";
import { useScreenShare } from "@daily-co/daily-react";
import { DisplayLayout } from "modules/audioVideo";
import { setLayout } from "modules/audioVideo/redux/slice";
import actions from "modules/broadcast/redux/actions";
import { selectNetworkIsConnected } from "modules/network/redux/selectors";

const StreamingContainer = (): JSX.Element | null => {
  const isConnected = useSelector(selectNetworkIsConnected);
  const isInBroadcast = useSelector(selectIsInBroadcast);
  const hasEventManagerAccess = useSelector(selectIsEventManager);
  const broadcastTransitionInProgress = useSelector(
    selectBroadcastTransitionInProgress,
  );
  const appDispatch = useAppDispatch();

  const broadcastTransitionComplete = useCallback(() => {
    appDispatch(actions.broadcastTransitionComplete());
  }, [appDispatch]);

  const onLocalScreenShareStopped = useCallback(() => {
    broadcastTransitionComplete();
  }, [broadcastTransitionComplete]);

  const { t } = useI18n(["common", "event"]);
  const { stopScreenShare, isSharingScreen } = useScreenShare({
    onLocalScreenShareStopped,
  });
  const { closeWhiteboard: closeBroadcastWhiteboard } =
    useBroadcastWhiteboardActions();
  const { closeWhiteboard: closeRoomWhiteboard } = useRoomWhiteboardActions();
  const theaterId = useSelector(selectCurrentTheaterId);
  const roomId = useSelector(selectCurrentRoomId);
  const isPreviouslyInBroadcast = usePrevious(isInBroadcast);

  useEffect(() => {
    stopScreenShare();

    if (isInBroadcast && theaterId) {
      closeRoomWhiteboard({ roomId });
    } else {
      appDispatch(setLayout(DisplayLayout.Map));
      appDispatch(hideCaptions());
      appDispatch(hideLiveCaptions());
      if (isPreviouslyInBroadcast && theaterId) {
        closeBroadcastWhiteboard();
        trackActionEnd(Actions.PRESENTATION_MODE_END, undefined, {
          theaterId,
        });
      }
    }

    if (!isSharingScreen) broadcastTransitionComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInBroadcast]);

  const isLoading = broadcastTransitionInProgress || isInBroadcast === null;

  if (!isConnected) {
    return null;
  }

  if (isLoading) {
    return <PageLoader message={t("loading")} fullScreen />;
  }

  return (
    <>
      <TranscriptionSubscriber />
      {hasEventManagerAccess && <LiveStreamSubscriber />}
      {isInBroadcast ? <BroadcastContainer /> : <ConversationContainer />}
    </>
  );
};

export default StreamingContainer;
